/** Today screen: people rostered in the next ROSTER_DAYS days without a ticket they need. */
import { Icon } from "../../components/Icon";
import { plural } from "../../components/PageHeader";
import { EmptyState, ErrorState, SkeletonLines } from "../../components/States";
import { listCrew } from "../../lib/data";
import { fmtDay } from "../../lib/format";
import { missingTickets, ROSTER_DAYS, rosteredCrew, TICKETS, ticketShort } from "../../lib/tickets";
import { useAsync } from "../../lib/useAsync";

export const ticketsInUse = () => TICKETS.length > 0;

export function useMissingTickets() {
  return useAsync(async () => {
    if (!ticketsInUse()) return [];
    const [list, rostered] = await Promise.all([listCrew(), rosteredCrew()]);
    return missingTickets(list, rostered);
  });
}
type State = ReturnType<typeof useMissingTickets>;

/** Props for the Today screen's KPI tile. */
export const missingTicketsKpi = (s: State) => ({
  label: "Missing tickets",
  value: s.data?.length,
  loading: s.loading,
  failed: Boolean(s.error),
  sub: s.data ? (s.data.length ? `Rostered in the next ${ROSTER_DAYS} days` : "Everyone rostered holds what they need") : undefined,
  href: "#/crew",
});

export function MissingTicketsCard({ state: s }: { state: State }) {
  const missing = s.data;
  return (
    <div className="card card--alert">
      <div className="card__head">
        <h2 className="card__title">Missing tickets</h2>
        <a className="card__link" href="#/crew">Crew <Icon name="arrow" size={16} /></a>
      </div>
      {s.loading && <SkeletonLines rows={2} />}
      {s.error && <ErrorState message={s.error} onRetry={s.reload} />}
      {missing && missing.length === 0 && <EmptyState icon="check" title="All tickets held">Everyone rostered in the next {ROSTER_DAYS} days holds the tickets they need.</EmptyState>}
      {missing && missing.length > 0 && (
        <ul className="list">
          {missing.slice(0, 6).map((m) => (
            <li key={m.crewId} className="list__row">
              <span>
                <strong>{m.name}</strong>
                <span className="sub">No {m.missing.map(ticketShort).join(", ")}{m.leadingHand ? " (leading hand)" : ""}</span>
              </span>
              <span className="list__value list__value--primary">{fmtDay(m.firstShift)}</span>
            </li>
          ))}
          {missing.length > 6 && <li className="list__more"><a href="#/crew">{plural(missing.length - 6, "more person", "more people")}</a></li>}
        </ul>
      )}
    </div>
  );
}
